"use client"

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import { Trash2, ChevronDown, ArrowLeft, Loader2, ClipboardCheck } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import type { CategoryGoodItem } from "@/lib/types/goods";
import { applicableCardItems, cardItemUnitPrice, recordedUnitPrice } from "@/lib/appointment/techCards";
import { cardWrittenOff, leftAfter, materialsShare, visitPrice } from "@/lib/appointment/consumablesStock";
import { appendLines, newLineId } from "@/lib/appointment/consumableLines";
import { useAppointmentModalConsumablesInfo } from "@/hooks/modal/useAppointmentModalConsumablesInfo";
import { useGoodsCategories } from "@/hooks/modal/useGoodsCategories";
import { useCategoryGoods } from "@/hooks/modal/useCategoryGoods";
import { useGoodsSearch } from "@/hooks/modal/useGoodsSearch";
import { useCurrencySymbol } from "@/hooks/shared/useCurrencySymbol";

// Flat consumable item type for appointment.consumables
interface FlatConsumable {
  id: number;
  technological_card_id: number;
  storage_id: number;
  good_id: number;
  amount: number;
  price: number;
  unitPrice: number; // Price per unit, used to recalculate price when amount changes
  service_id: number;
  title?: string; // Optional: good title for display
  recordedAmount?: number; // what the line had when the visit opened (the shelf already lost it)
}

/**
 * Multi-level category goods selector.
 * Step 1: Shows list of categories with goods count
 * Step 2: After selecting a category, shows goods in that category
 */
function CategoryGoodsSelector({
  onSelect,
  storages,
  selectedStorageId,
  onStorageChange,
}: {
  onSelect: (good: CategoryGoodItem) => void;
  storages: { id: number; title: string }[];
  selectedStorageId: number | null;
  onStorageChange: (id: number) => void;
}) {
  const { t } = useTranslation();
  const currencySymbol = useCurrencySymbol();
  const [open, setOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  // Global search mode: only on category level.
  // Inside a selected category we keep local filtering, regardless of length.
  const isSearchMode = !selectedCategoryId && search.length >= 3;

  // Search hook for goods search
  const {
    data: searchResults,
    isLoading: searchLoading,
  } = useGoodsSearch(selectedStorageId, isSearchMode ? search : "");

  // Fetch categories when popover opens
  const {
    data: categories,
    isLoading: categoriesLoading,
  } = useGoodsCategories(open);

  // Fetch goods when a category is selected
  const {
    data: categoryGoods,
    isLoading: goodsLoading,
  } = useCategoryGoods(selectedCategoryId);

  // Reset state when popover closes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSelectedCategoryId(null);
      setSearch("");
    }
  };

  // Handle back button - return to categories list
  const handleBack = () => {
    setSelectedCategoryId(null);
    setSearch("");
  };

  // Handle category selection
  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    setSearch("");
  };

  // Handle good selection
  const handleGoodSelect = (good: CategoryGoodItem) => {
    onSelect(good);
    handleOpenChange(false);
  };

  // Filter categories by search
  const filteredCategories = useMemo(() => {
    if (!categories) return [];
    if (!search || search.length < 2) return categories;
    const lower = search.toLowerCase();
    return categories.filter((c) => c.name.toLowerCase().includes(lower));
  }, [categories, search]);

  // Filter goods by search
  const filteredGoods = useMemo(() => {
    if (!categoryGoods) return [];
    if (!search || search.length < 2) return categoryGoods;
    const lower = search.toLowerCase();
    return categoryGoods.filter((g) => g.title.toLowerCase().includes(lower));
  }, [categoryGoods, search]);

  // Get selected category name for header
  const selectedCategory = categories?.find((c) => c.id === selectedCategoryId);

  return (
    <div className="flex flex-wrap items-center gap-2">

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-8 text-xs w-full sm:w-48 justify-between font-normal border-input bg-transparent"
          >
            <span className="truncate">{t("appointment.consumables.add_consumable", "Add consumable")}</span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command shouldFilter={false}>
            {/* Header with back button when viewing goods */}
            {selectedCategoryId && selectedCategory && (
              <div className="flex items-center gap-2 px-2 py-2 border-b">
                <button
                  onClick={handleBack}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="text-xs font-medium truncate">
                  {selectedCategory.name}
                </span>
              </div>
            )}

            <CommandInput
              placeholder={selectedCategoryId ? t("appointment.consumables.search_product", "Search product...") : t("appointment.consumables.search_category", "Search category...")}
              value={search}
              onValueChange={setSearch}
              className="text-xs h-9"
            />

            <CommandList
              className="max-h-64 overflow-y-auto overscroll-contain touch-pan-y"
              onWheelCapture={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {/* Loading state */}
              {(categoriesLoading || goodsLoading) && (
                <div className="py-4 flex justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}

              {/* Search results (when search has 3+ chars) */}
              {isSearchMode && !searchLoading && (
                <>
                  <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
                    {searchResults?.length === 0 ? t("common.nothing_found", "Nothing found") : ""}
                  </CommandEmpty>
                  <CommandGroup className="p-1">
                    {searchResults?.map((good) => (
                      <CommandItem
                        key={good.id}
                        value={String(good.id)}
                        onSelect={() => handleGoodSelect(good)}
                        className="text-xs"
                      >
                        <div className="flex flex-col">
                          <span className="truncate">{good.title}</span>
                          <span className="text-muted-foreground text-[10px]">
                            {good.price.toFixed(2)} {currencySymbol} / {good.unit}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {/* Search loading state */}
              {isSearchMode && searchLoading && (
                <div className="py-4 flex justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}

              {/* Categories list (when no category selected and not in search mode) */}
              {!selectedCategoryId && !categoriesLoading && !isSearchMode && (
                <>
                  <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
                    {search.length < 2 && categories?.length === 0
                      ? t("appointment.consumables.no_categories", "No categories")
                      : search.length < 2
                        ? t("appointment.consumables.enter_2_chars", "Enter 2 characters...")
                        : t("appointment.consumables.category_not_found", "Category not found")}
                  </CommandEmpty>
                  <CommandGroup className="p-1">
                    {filteredCategories.map((category) => (
                      <CommandItem
                        key={category.id}
                        value={String(category.id)}
                        onSelect={() => handleCategorySelect(category.id)}
                        className="text-xs flex justify-between items-center"
                      >
                        <span className="truncate">{category.name}</span>
                        <span className="text-muted-foreground ml-2">
                          ({category.goods_count})
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {/* Goods list (when category is selected and not in search mode) */}
              {selectedCategoryId && !goodsLoading && !isSearchMode && (
                <>
                  <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
                    {search.length < 2 && categoryGoods?.length === 0
                      ? t("appointment.consumables.no_products_in_category", "No products in category")
                      : search.length < 2
                        ? t("appointment.consumables.enter_2_chars", "Enter 2 characters...")
                        : t("appointment.consumables.product_not_found", "Product not found")}
                  </CommandEmpty>
                  <CommandGroup className="p-1">
                    {filteredGoods.map((good) => (
                      <CommandItem
                        key={good.id}
                        value={String(good.id)}
                        onSelect={() => handleGoodSelect(good)}
                        className="text-xs"
                      >
                        <div className="flex flex-col">
                          <span className="truncate">{good.title}</span>
                          <span className="text-muted-foreground text-[10px]">
                            {good.price.toFixed(2)} {currencySymbol} / {good.unit}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <div className="p-2 text-xs text-gray-500 rounded">{t("appointment.consumables.storage_label", "Storage:")}</div>
      {/* Storage dropdown */}
      <Select
        value={selectedStorageId ? String(selectedStorageId) : undefined}
        onValueChange={(val) => onStorageChange(Number(val))}
      >
        <SelectTrigger className="h-8 text-xs w-32">
          <SelectValue placeholder={t("appointment.consumables.storage", "Storage")} />
        </SelectTrigger>
        <SelectContent>
          {storages.map((storage) => (
            <SelectItem key={storage.id} value={String(storage.id)}>
              {storage.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function ConsumablesSection() {
  const { t } = useTranslation();
  const currencySymbol = useCurrencySymbol();
  // Get normalized consumables data directly from the hook
  const { data: consumablesData, isLoading, error } = useAppointmentModalConsumablesInfo();
  console.log('--\u003e consumablesData', consumablesData);
  const { appointment, updateAppointment } = useAppointmentModal();

  // Track if we've already initialized consumables from backend data
  const [hasInitialized, setHasInitialized] = useState(false);

  // Extract storages from consumablesData and sort: service storages first, then by id ascending
  const storages = useMemo(() => {
    if (!consumablesData?.storage) return [];
    // storage could be array or object
    let storageList: { id: number; title: string; for_service?: number }[];
    if (Array.isArray(consumablesData.storage)) {
      storageList = consumablesData.storage;
    } else {
      storageList = Object.values(consumablesData.storage);
    }
    // Sort: for_service=1 first, then by id ascending
    return storageList.sort((a, b) => {
      const aForService = a.for_service ?? 0;
      const bForService = b.for_service ?? 0;
      // Service storages first (descending for_service)
      if (bForService !== aForService) {
        return bForService - aForService;
      }
      // Then by id ascending
      return a.id - b.id;
    });
  }, [consumablesData]);

  // State for selected storage, default to first
  const [selectedStorageId, setSelectedStorageId] = useState<number | null>(null);

  // Initialize storage when data loads
  useEffect(() => {
    if (storages.length > 0 && selectedStorageId === null) {
      setSelectedStorageId(storages[0].id);
    }
  }, [storages, selectedStorageId]);

  // Populate appointment.consumables with recorded data if available, or set to empty array if none
  useEffect(() => {
    if (
      consumablesData &&
      Array.isArray(consumablesData.recorded) &&
      !hasInitialized &&
      (!appointment?.consumables || (Array.isArray(appointment.consumables) && appointment.consumables.length === 0))
    ) {
      if (consumablesData.recorded.length > 0) {
        console.log('🔄 Initializing consumables from backend data');
        const recordedConsumables = consumablesData.recorded.map((item: any) => {
          const amount = Math.abs(item.amount) || 1;
          // a recorded line's price is a UNIT price; the line total is ours
          const unitPrice = recordedUnitPrice(item);
          return {
            id: item.goods_transaction_id || newLineId(),
            technological_card_id: 0,
            storage_id: item.storage_id,
            good_id: item.good_id,
            amount,
            price: unitPrice * amount,
            unitPrice,
            service_id: item.service_id || 0,
            title: item.good?.title, // Extract title from backend response
            recordedAmount: amount,
          };
        });
        updateAppointment({ consumables: recordedConsumables });
      } else {
        console.log('🔄 No consumables fetched, setting to empty array');
        updateAppointment({ consumables: [] });
      }
      setHasInitialized(true);
    }
  }, [consumablesData, appointment?.consumables, hasInitialized, updateAppointment]);

  // Reset initialization flag when appointment changes
  useEffect(() => {
    setHasInitialized(false);
  }, [appointment?.id]);

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">{t("appointment.consumables.loading", "Loading consumables...")}</div>;
  }
  if (error) {
    return <div className="p-6 text-destructive">{t("appointment.consumables.error", "Error loading consumables.")}</div>;
  }
  if (!consumablesData) {
    return <div className="p-6 text-muted-foreground">{t("appointment.consumables.no_data", "No consumables data.")}</div>;
  }
  if (!appointment) {
    return <div className="p-6 text-muted-foreground">{t("appointment.create.no_data", "No appointment data.")}</div>;
  }

  // Use appointment.consumables as a flat array
  const consumables: FlatConsumable[] = Array.isArray(appointment.consumables)
    ? appointment.consumables
    : [];

  // Debug: Log current appointment and consumables
  console.log('🔍 Current appointment object:', appointment);
  console.log('📦 Current consumables array:', consumables);

  // Get appointment services
  const services = appointment.services || [];

  // Group consumables by service_id
  const consumablesByService = consumables.reduce((acc, consumable) => {
    const serviceId = consumable.service_id;
    if (!acc[serviceId]) {
      acc[serviceId] = [];
    }
    acc[serviceId].push(consumable);
    return acc;
  }, {} as Record<number, FlatConsumable[]>);

  // Handler: Add consumables (a good, or a card's lines) to a specific service - after the
  // lines already there, in the order given; the same good again is a line of its own
  const handleAddConsumables = (newItems: FlatConsumable[]) => {
    updateAppointment({ consumables: appendLines(consumables, newItems) });
  };

  // Handler: Remove a consumable by id
  const handleRemoveConsumable = (id: number) => {
    console.log('🗑️ Attempting to remove consumable with id:', id);
    console.log('📋 Current consumables:', consumables);
    console.log('🔍 Consumables IDs:', consumables.map(c => ({ id: c.id, type: typeof c.id })));

    const newList = consumables.filter((c) => c.id !== id);
    console.log('✅ New list after filter:', newList);
    console.log('📊 Length before:', consumables.length, 'Length after:', newList.length);

    updateAppointment({ consumables: newList });
  };

  // Handler: Change amount for a consumable
  const handleChangeAmount = (id: number, newAmount: number) => {
    const newList = consumables.map((c) =>
      c.id === id ? { ...c, amount: newAmount, price: c.unitPrice * newAmount } : c
    );
    updateAppointment({ consumables: newList });
  };

  // For UI: get all technological cards
  const allTechCards: any[] = consumablesData.technological_cards || [];
  // Storage plan ST8: the card each service follows, and the shelf after this visit
  const serviceCards: Record<string, number> = consumablesData.service_cards || {};
  const left = leftAfter(consumables, consumablesData.stock);

  // Handler to add a consumable from a category good to a specific service
  const handleAddGoodFromCategory = (good: CategoryGoodItem, serviceId: number) => {
    const newItem: FlatConsumable = {
      id: newLineId(), // a new line's own id (consumableLines.ts)
      technological_card_id: 0,
      storage_id: good.storage_id,
      good_id: good.good_id,
      amount: 1,
      price: good.price,
      unitPrice: good.price, // Store unit price for recalculation
      service_id: serviceId,
      title: good.title, // Store the title for display
    };
    handleAddConsumables([newItem]);
  };

  // Handler to add all items from a selected technological card to a specific service
  const handleAddFromCard = (card: any, serviceId: number) => {
    if (!card || !Array.isArray(card.technological_card_items)) return;
    // a deleted good stays on the card but takes no new write-offs
    // all of the card's lines in one update: added one at a time from the same list, each
    // replaced the last and only the card's final line stayed
    const items = applicableCardItems<any>(card.technological_card_items).map((item: any): FlatConsumable => {
      const amount = item.amount || 1;
      const unitPrice = cardItemUnitPrice(item);
      return {
        id: newLineId(), // a new line's own id (consumableLines.ts)
        technological_card_id: item.technological_card_id,
        storage_id: item.storage_id,
        good_id: item.good_id,
        amount: amount,
        price: unitPrice * amount,
        unitPrice: unitPrice, // Store unit price for recalculation
        service_id: serviceId,
        title: item.good?.title, // Store the title for display
      };
    });
    handleAddConsumables(items);
  };

  // Calculate total cost for all consumables - just sum the price column
  const totalCost = consumables.reduce((sum: number, c: FlatConsumable) =>
    sum + (typeof c.price === "number" ? c.price : 0), 0);
  const share = materialsShare(totalCost, visitPrice(services as any[]));

  // Calculate cost per service - just sum the price column
  const getServiceCost = (serviceId: number) => {
    const serviceConsumables = consumablesByService[serviceId] || [];
    return serviceConsumables.reduce((sum: number, c: FlatConsumable) =>
      sum + (typeof c.price === "number" ? c.price : 0), 0);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <h3 className="text-base font-medium mb-2">{t("appointment.consumables.title", "Consumables")}</h3>
      <p className="text-xs text-gray-600 mb-4">
        {t("appointment.consumables.description", "Consumables by service. Each service has its own consumables list.")}
      </p>

      {/* Display consumables grouped by service */}
      {services.map((service) => {
        const serviceId = Number(service.id);
        const serviceConsumables = consumablesByService[serviceId] || [];
        const serviceCost = getServiceCost(serviceId);

        return (
          <div key={serviceId} className="mb-6 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-gray-900">
                {service.title}
              </h4>
              {serviceCost > 0 && (
                <span className="text-sm text-gray-600">
                  {serviceCost.toFixed(2)} {currencySymbol}
                </span>
              )}
            </div>

            {/* The card this service follows (the master's own, else the service's): one click */}
            {(() => {
              const card = allTechCards.find((c) => c.id === serviceCards[String(serviceId)]);
              if (!card || cardWrittenOff(consumables, serviceId, card.id)) return null;
              return (
                <Button
                  type="button"
                  size="sm"
                  className="mb-3 h-8 text-xs gap-1.5 w-full sm:w-auto"
                  onClick={() => handleAddFromCard(card, serviceId)}
                >
                  <ClipboardCheck className="h-4 w-4" />
                  {t("appointment.consumables.write_off_by_card", "Write off by card «{{title}}»", { title: card.title })}
                </Button>
              );
            })()}

            {/* Controls row: add from goods or card for this service */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-3">
              {/* Dropdown to add from technological card */}
              <Select
                onValueChange={(val) => {
                  const card = allTechCards.find((c) => String(c.id) === val);
                  if (card) handleAddFromCard(card, serviceId);
                }}
              >
                <SelectTrigger className="h-8 text-xs w-full sm:w-48">
                  <SelectValue placeholder={t("appointment.consumables.add_from_tech_card", "Add from tech card")} />
                </SelectTrigger>
                <SelectContent>
                  {allTechCards.map((card) => (
                    <SelectItem key={card.id} value={String(card.id)}>
                      {card.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Dropdown to add from category goods */}
              <CategoryGoodsSelector
                onSelect={(good) => handleAddGoodFromCategory(good, serviceId)}
                storages={storages}
                selectedStorageId={selectedStorageId}
                onStorageChange={setSelectedStorageId}
              />
            </div>

            {/* Consumables Table for this service */}
            {serviceConsumables.length > 0 ? (
              <div className="mb-2">
                {/* Desktop header - hidden on mobile */}
                <div className="hidden sm:grid grid-cols-7 gap-2 mb-2 text-xs text-gray-500">
                  <div className="col-span-4">{t("appointment.consumables.name", "Name")}</div>
                  <div className="col-span-1 text-right">{t("appointment.consumables.quantity", "Qty")}</div>
                  <div className="col-span-1 text-right">{t("appointment.consumables.price", "Price")}</div>
                  <div className="col-span-1"></div>
                </div>
                {serviceConsumables.map((c: FlatConsumable) => {
                  // Use stored title, or lookup from tech cards, or fallback to good_id
                  let goodTitle: string | number = c.title || c.good_id;
                  if (!c.title) {
                    for (const card of allTechCards) {
                      const item = card.technological_card_items?.find(
                        (i: any) => i.good_id === c.good_id || i.good?.id === c.good_id
                      );
                      if (item?.good?.title) {
                        goodTitle = item.good.title;
                        break;
                      }
                    }
                  }
                  return (
                    <div
                      key={c.id}
                      className="flex flex-col sm:grid sm:grid-cols-7 gap-2 mb-3 sm:mb-2 p-2 sm:p-0 bg-gray-50 sm:bg-transparent rounded sm:rounded-none sm:items-center"
                    >
                      {/* Title - full width on mobile; what the shelf keeps after this visit */}
                      <div className="sm:col-span-4 text-sm font-medium sm:font-normal">
                        {goodTitle}
                        {left[c.id] && (
                          <div
                            className={
                              "text-xs font-normal " +
                              (left[c.id].tone === "below_zero"
                                ? "text-rose-600"
                                : left[c.id].tone === "low"
                                  ? "text-amber-600"
                                  : "text-gray-500")
                            }
                          >
                            {t("appointment.consumables.left_after", "left after this: {{n}} {{unit}}", {
                              n: left[c.id].left.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                              unit: left[c.id].unit,
                            })}
                          </div>
                        )}
                      </div>

                      {/* Amount and Price row on mobile */}
                      <div className="flex items-center justify-between sm:contents gap-2">
                        <div className="flex items-center gap-2 sm:col-span-1 sm:justify-end">
                          <span className="text-xs text-gray-500 sm:hidden">{t("appointment.consumables.quantity_label", "Qty:")}</span>
                          <Input
                            type="number"
                            value={c.amount}
                            className="w-16 text-center border-gray-300 rounded h-8 text-xs px-1"
                            onChange={(e) =>
                              handleChangeAmount(c.id, Number(e.target.value))
                            }
                          />
                        </div>
                        <div className="flex items-center gap-2 sm:col-span-1 sm:justify-end">
                          <span className="text-xs text-gray-500 sm:hidden">{t("appointment.consumables.price_label", "Price:")}</span>
                          <Input
                            type="text"
                            value={c.price.toFixed(2)}
                            className="w-16 text-center border-gray-300 rounded h-8 text-xs px-1 bg-muted"
                            readOnly
                          />
                        </div>
                        <div className="sm:col-span-1 flex justify-end">
                          <button
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => handleRemoveConsumable(c.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-2 text-xs text-gray-500 bg-gray-50 rounded">
                {t("appointment.consumables.no_consumables_for_service", "No consumables for this service")}
              </div>
            )}
          </div>
        );
      })}

      {/* Overall Totals */}
      {totalCost > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="text-right">
            <div className="text-sm text-gray-600">{t("appointment.consumables.total_to_write_off", "Total to write off:")}</div>
            <div className="text-lg font-medium">{totalCost.toFixed(2)} {currencySymbol}</div>
            {share !== null && (
              <div className={"text-xs " + (share > 10 ? "text-amber-600" : "text-gray-500")}>
                {t("appointment.consumables.materials_share", "materials: {{n}}% of the visit's price", { n: share })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Show message if no services */}
      {services.length === 0 && (
        <div className="p-4 text-center text-gray-500 bg-gray-50 rounded">
          {t("appointment.consumables.no_services", "No services in appointment")}
        </div>
      )}
    </div>
  );
}
